import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, InputGroup } from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const { isRTL } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    country: 'SA',
    mobileDigits: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [showRegisterPrompt, setShowRegisterPrompt] = useState(false);

  // Check for message from URL
  useEffect(() => {
    const message = searchParams.get('message');
    if (message) {
      setInfoMessage(decodeURIComponent(message));
    }
  }, [searchParams]);

  const handleChange = (e: any) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Reset register prompt when user changes input
    if (showRegisterPrompt) setShowRegisterPrompt(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setShowRegisterPrompt(false);

    try {
      const code = formData.country === 'SA' ? '+966' : '+249';
      const digitsOnly = /^[0-9]+$/.test(formData.mobileDigits);
      if (!digitsOnly) {
        setError(t('mobileDigitsOnly') as string);
        setLoading(false);
        return;
      }
      let local = formData.mobileDigits;
      if (local.length === 10 && local.startsWith('0')) {
        local = local.substring(1);
      }
      if (local.length !== 9) {
        setError(t('mobileLengthInvalid', { count: 9 }) as string);
        setLoading(false);
        return;
      }
      const fullMobile = `${code}${local}`;
      const result = await login(fullMobile, formData.password);

      if (result.success) {
        // Check if there's a pending product request
        const pendingRequest = localStorage.getItem('pendingProductRequest');
        const redirect = searchParams.get('redirect') || '/';
        
        if (pendingRequest && redirect.includes('product-request')) {
          // Redirect to product request page to auto-submit
          navigate('/product-request');
        } else {
          navigate(redirect);
        }
      } else {
        // Check if the error might indicate "User not found"
        // Since we can't be 100% sure with 401, we offer the registration link if login fails
        setError(result.message || t('loginErrorInvalid') as string);
        // Show registration prompt for any login failure to help unregistered users
        setShowRegisterPrompt(true);
      }
    } catch (err) {
      setError(t('loginErrorGeneric') as string);
    }

    setLoading(false);
  };

  const handleRegisterRedirect = () => {
    navigate('/register', { 
      state: { 
        mobileDigits: formData.mobileDigits,
        country: formData.country 
      } 
    });
  };

  return (
    <>
      <Helmet>
        <title>{t('login')} - MIC Green Store</title>
        <meta name="description" content="Sign in to your MIC Green Store account" />
        <meta property="og:title" content={`${t('login')} - MIC Green Store`} />
        <meta property="og:description" content="Sign in to your MIC Green Store account" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />
      </Helmet>

      <Container className="py-5" style={{ marginTop: '120px', direction: isRTL ? 'rtl' : 'ltr' }}>
        <Row className="justify-content-center">
          <Col md={6} lg={5}>
            <Card className="shadow-lg border-0" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <Card.Body className="p-5">
                <div className="text-center mb-4">
                  <div className="mb-3">
                    <i className="fas fa-sign-in-alt fa-3x text-primary mb-3"></i>
                  </div>
                  <h2 className="fw-bold mb-2" style={{ 
                    background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #059669 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}>
                    {t('loginForm')}
                  </h2>
                  <p className="text-muted">{t('welcomeBack')}</p>
                </div>
                
                {error && <Alert variant="danger">{error}</Alert>}
                {infoMessage && (
                  <Alert variant="info" className="mb-3">
                    <i className="fas fa-info-circle me-2"></i>
                    {infoMessage}
                  </Alert>
                )}
                
                {showRegisterPrompt && (
                  <Alert variant="warning" className="mb-4">
                    <div className="d-flex flex-column align-items-center text-center">
                      <p className="mb-2 fw-bold">{t('notRegisteredPrompt')}</p>
                      <Button 
                        variant="outline-dark" 
                        size="sm" 
                        onClick={handleRegisterRedirect}
                        className="fw-bold"
                      >
                        {t('completeRegistration')}
                      </Button>
                    </div>
                  </Alert>
                )}
                
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold">
                      <i className="fas fa-mobile-alt me-2 text-primary"></i>
                      {t('mobileNumber')}
                    </Form.Label>
                    <InputGroup dir="ltr">
                      <Form.Select
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        className="py-2 bg-light"
                        style={{ maxWidth: '140px', fontWeight: 'bold' }}
                      >
                        <option value="SA">🇸🇦 +966</option>
                        <option value="SD">🇸🇩 +249</option>
                      </Form.Select>
                      <Form.Control
                        type="tel"
                        name="mobileDigits"
                        value={formData.mobileDigits}
                        onChange={handleChange}
                        placeholder={t('enterMobileDigits') as string}
                        required
                        className="py-2"
                        inputMode="numeric"
                      />
                    </InputGroup>
                  </Form.Group>
                  
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold">
                      <i className="fas fa-lock me-2 text-primary"></i>
                      {t('password')}
                    </Form.Label>
                    <Form.Control
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder={t('passwordPlaceholder') as string}
                      required
                      className="py-2"
                      dir={isRTL ? 'rtl' : 'ltr'}
                    />
                  </Form.Group>
                  
                  <Button 
                    variant="primary" 
                    type="submit" 
                    className="w-100 mb-3 py-2 fw-semibold"
                    disabled={loading}
                    style={{
                      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                      border: 'none',
                      fontSize: '1.1rem'
                    }}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {t('loggingIn')}
                      </>
                    ) : (
                      <>
                        <i className="fas fa-sign-in-alt me-2"></i>
                        {t('signIn')}
                      </>
                    )}
                  </Button>
                </Form>
                
                <div className="text-center mt-4">
                  <p className="mb-0">
                    {t('dontHaveAccount')}{' '}
                    <Link 
                      to="/register" 
                      onClick={(e) => {
                        e.preventDefault();
                        handleRegisterRedirect();
                      }}
                      style={{ 
                        color: '#059669', 
                        textDecoration: 'none',
                        fontWeight: '600'
                      }}
                    >
                      {t('signUp')}
                    </Link>
                  </p>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Login;
